'use client'
import {
  createContext,
  Suspense,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react'
import { useQuery } from '@tanstack/react-query'
import { RibbonSchema, RibbonSlug } from 'cms/schemaTypes/ribbons/ribbon'
import { usePathname, useSearchParams } from 'next/navigation'

import { internalApi, QueryKey } from 'shared/api'
import { isClient } from 'shared/utils/common'
import { PATHS } from 'shared/utils/paths'
import { LocalStorage } from 'shared/utils/storage'

const CACHE_TIME_MS = 1000 * 60 * 60 * 24

export const RibbonContext = createContext<{
  ribbon: RibbonSchema | null
  hideRibbon: () => void
  actions: {
    setRibbon: (ribbon: RibbonSchema | null) => void
    setHideRibbon: (fn: () => void) => void
  }
}>({
  ribbon: null,
  hideRibbon: () => {},
  actions: {
    setRibbon: () => {},
    setHideRibbon: () => {}
  }
})

function RibbonLogic() {
  const { actions } = useContext(RibbonContext)
  const pathname = usePathname()
  const { query } = useRibbonQuery(pathname || '')

  const hideRibbon = useCallback(() => {
    if (query.data?.key) {
      RibbonStorageService.onCloseRibbon(query.data.key)
      actions?.setRibbon(null)
    }
  }, [query.data, actions])

  useEffect(() => {
    if (query.isLoading) return

    const ribbonData = query.data || null

    if (!ribbonData) return actions?.setRibbon(null)

    const isClosedRibbon = RibbonStorageService.isClosed(query.data?.key)

    actions?.setRibbon(isClosedRibbon ? null : ribbonData)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.data, actions])

  useEffect(() => {
    actions?.setHideRibbon(() => hideRibbon)
  }, [hideRibbon, actions])

  return null
}

export function RibbonProvider({ children }: { children: React.ReactNode }) {
  const [ribbon, setRibbon] = useState<RibbonSchema | null>(null)
  const [hideRibbon, setHideRibbon] = useState<() => void>(() => () => {})

  const actions = useMemo(
    () => ({
      setRibbon,
      setHideRibbon
    }),
    []
  )

  return (
    <RibbonContext.Provider value={{ ribbon, hideRibbon, actions }}>
      {children}
      <Suspense>
        <RibbonLogic />
      </Suspense>
    </RibbonContext.Provider>
  )
}

const getRibbonSlug = (pathname: string | null) => {
  if (!pathname) {
    return 'general'
  }

  if (pathname?.startsWith(PATHS.landing.replace('[slug]', ''))) {
    return 'landing'
  }

  switch (pathname) {
    case '/koszyk':
      return 'basket'
    case '/dostawa':
      return 'basket'
    case '/platnosc':
      return 'basket'
    case '/projekt-eko':
      return 'landing'
    default:
      return 'general'
  }
}

export const useRibbonQuery = (pathname: string) => {
  const params = useSearchParams()

  // for testing
  const { token, key } = useMemo(() => {
    return {
      token: params?.get('token'),
      key: params?.get('key')
    }
  }, [params])

  const slug = useMemo((): RibbonSlug => {
    return getRibbonSlug(pathname)
  }, [pathname])

  const query = useQuery({
    //token and key are used only for testing(to not show ribbon into customers)
    queryKey: [QueryKey.ribbon, slug, token, key],
    queryFn: async () => {
      if (!slug) return null

      const response = await internalApi.get<RibbonSchema>('/ribbon', {
        params: { slug, token, key }
      })

      return response.data
    },
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    staleTime: CACHE_TIME_MS,
    gcTime: isClient() ? CACHE_TIME_MS : Infinity
  })

  return { query, slug }
}

class RibbonStorageService {
  static STORAGE_KEY = 'ribbonClosed'

  static buildKey(key: string) {
    return `${RibbonStorageService.STORAGE_KEY}_${key}`
  }

  static onCloseRibbon(key: string) {
    const ribbonStorage = new LocalStorage(
      RibbonStorageService.buildKey(key),
      'session'
    )

    ribbonStorage.set(true)
  }

  static isClosed(key: string | undefined) {
    if (!key) return false

    const ribbonStorage = new LocalStorage(
      RibbonStorageService.buildKey(key),
      'session'
    )
    const storedClosedRibbons = ribbonStorage.get(false)

    return storedClosedRibbons
  }
}
